import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Breadcrumbs,
  Link,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { AxiosRequestConfig } from 'axios';

// Add axios base URL configuration
axios.defaults.baseURL = 'http://localhost:5000';

// Add axios interceptor for authentication
axios.interceptors.request.use((config: AxiosRequestConfig) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = token;
  }
  return config;
});

interface Area {
  _id: string;
  name: string;
  type: 'province' | 'city' | 'town' | 'district' | 'ward';
  code: string;
  parentCode: string | null;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  status: 'active' | 'inactive';
}

interface AreaFormData {
  name: string;
  type: 'province' | 'city' | 'town' | 'district' | 'ward';
  code: string;
  parentCode: string | null;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  status: 'active' | 'inactive';
}

const initialFormData: AreaFormData = {
  name: '',
  type: 'province',
  code: '',
  parentCode: null,
  coordinates: {
    latitude: 0,
    longitude: 0
  },
  status: 'active'
};

const AreasMenu: React.FC = () => {
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentParent, setCurrentParent] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<Area[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState<AreaFormData>(initialFormData);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Fetch areas based on current parent
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const params = new URLSearchParams();
        if (currentParent) {
          params.append('parentCode', currentParent);
        } else {
          params.append('type', 'province,city');
        }
        if (searchTerm) {
          params.append('search', searchTerm);
        }

        const response = await axios.get(`/api/areas?${params.toString()}`);
        setAreas(response.data.areas);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching areas:', error);
        toast.error('Error loading areas');
        setLoading(false);
      }
    };

    fetchAreas();
  }, [currentParent, searchTerm]);

  // Update breadcrumbs when parent changes
  useEffect(() => {
    const updateBreadcrumbs = async () => {
      if (!currentParent) {
        setBreadcrumbs([]);
        return;
      }

      try {
        let current = currentParent;
        const crumbs: Area[] = [];

        while (current) {
          const response = await axios.get(`/api/areas/${current}`);
          crumbs.unshift(response.data);
          current = response.data.parentCode;
        }

        setBreadcrumbs(crumbs);
      } catch (error) {
        console.error('Error updating breadcrumbs:', error);
      }
    };

    updateBreadcrumbs();
  }, [currentParent]);

  const handleNavigate = (code: string | null) => {
    setCurrentParent(code);
    setSearchTerm('');
  };

  const handleAdd = () => {
    setFormData({
      ...initialFormData,
      parentCode: currentParent,
      type: currentParent ? getChildType(breadcrumbs[breadcrumbs.length - 1]?.type) : 'province'
    });
    setEditingId(null);
    setDialogOpen(true);
  };

  const handleEdit = (area: Area) => {
    setFormData({
      name: area.name,
      type: area.type,
      code: area.code,
      parentCode: area.parentCode,
      coordinates: area.coordinates,
      status: area.status
    });
    setEditingId(area._id);
    setDialogOpen(true);
  };

  const handleDelete = async (code: string) => {
    if (!window.confirm('Are you sure you want to delete this area?')) {
      return;
    }

    try {
      await axios.delete(`/api/areas/${code}`);
      toast.success('Area deleted successfully');
      // Refresh the list
      const response = await axios.get(`/api/areas?parentCode=${currentParent || ''}`);
      setAreas(response.data.areas);
    } catch (error) {
      console.error('Error deleting area:', error);
      toast.error('Error deleting area');
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingId) {
        await axios.put(`/api/areas/${formData.code}`, formData);
        toast.success('Area updated successfully');
      } else {
        await axios.post('/api/areas', formData);
        toast.success('Area created successfully');
      }

      setDialogOpen(false);
      // Refresh the list
      const response = await axios.get(`/api/areas?parentCode=${currentParent || ''}`);
      setAreas(response.data.areas);
    } catch (error) {
      console.error('Error saving area:', error);
      toast.error('Error saving area');
    }
  };

  const getChildType = (parentType?: string): Area['type'] => {
    switch (parentType) {
      case 'province':
      case 'city':
        return 'district';
      case 'district':
        return 'ward';
      default:
        return 'province';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box mb={3}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Typography variant="h5">Administrative Areas</Typography>
            </Grid>
            {currentParent && (
              <Grid item>
                <IconButton onClick={() => handleNavigate(breadcrumbs[breadcrumbs.length - 2]?.code || null)}>
                  <ArrowBackIcon />
                </IconButton>
              </Grid>
            )}
            <Grid item xs>
              <Breadcrumbs>
                <Link
                  component="button"
                  variant="body1"
                  onClick={() => handleNavigate(null)}
                  color="inherit"
                >
                  Root
                </Link>
                {breadcrumbs.map((crumb, index) => (
                  <Link
                    key={crumb.code}
                    component="button"
                    variant="body1"
                    onClick={() => handleNavigate(crumb.code)}
                    color={index === breadcrumbs.length - 1 ? 'textPrimary' : 'inherit'}
                  >
                    {crumb.name}
                  </Link>
                ))}
              </Breadcrumbs>
            </Grid>
            <Grid item>
              <TextField
                size="small"
                placeholder="Search areas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAdd}
              >
                Add Area
              </Button>
            </Grid>
          </Grid>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {areas.map((area) => (
                <TableRow key={area._id}>
                  <TableCell>
                    <Link
                      component="button"
                      onClick={() => handleNavigate(area.code)}
                      color="primary"
                    >
                      {area.name}
                    </Link>
                  </TableCell>
                  <TableCell>{area.type}</TableCell>
                  <TableCell>{area.code}</TableCell>
                  <TableCell>{area.status}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(area)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(area.code)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {areas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No areas found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingId ? 'Edit Area' : 'Add Area'}</DialogTitle>
          <DialogContent>
            <Box mt={2}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Type"
                    select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as Area['type'] })}
                  >
                    <MenuItem value="province">Province</MenuItem>
                    <MenuItem value="city">City</MenuItem>
                    <MenuItem value="town">Town</MenuItem>
                    <MenuItem value="district">District</MenuItem>
                    <MenuItem value="ward">Ward</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    disabled={!!editingId}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Latitude"
                    type="number"
                    value={formData.coordinates.latitude}
                    onChange={(e) => setFormData({
                      ...formData,
                      coordinates: {
                        ...formData.coordinates,
                        latitude: parseFloat(e.target.value)
                      }
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Longitude"
                    type="number"
                    value={formData.coordinates.longitude}
                    onChange={(e) => setFormData({
                      ...formData,
                      coordinates: {
                        ...formData.coordinates,
                        longitude: parseFloat(e.target.value)
                      }
                    })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Status"
                    select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} color="primary" variant="contained">
              {editingId ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default AreasMenu; 